// src/services/liveFollow.js — following someone's live location in the browser.
//
// 🟥 THE WEB SAID "NOT AVAILABLE". A live location drew a red dot and "open Allo on
//    your phone", with no map at all — not even the first position, which the message
//    itself carries.
// 🟢 The browser follows the session document the phone writes (liveLocations/{id}):
//    the newest sealed fix is opened with this browser's own device key, the map
//    moves, the card says how old the position is (services/liveRules.js, the same
//    rule as the phone), and Refresh asks the SERVER — not the cache — for the
//    newest fix when a weak connection has left the live listener behind.
//    Sharing a live location from the web is still phone-only (a closed tab cannot
//    keep sending).
import { useEffect, useRef, useState, useCallback } from 'react';
import { doc, onSnapshot, getDocFromServer } from 'firebase/firestore';
import { db } from '../config/firebase';
import { unsealMediaKey } from '../crypto/e2ee';
import { toMillis, liveExpired, validFix } from './liveRules';

async function readFix(d, senderId) {
  if (!d || !d.locationPerDeviceKeys) return null;
  try {
    const p = await unsealMediaKey(d.locationPerDeviceKeys, d.senderId || senderId, d.senderDeviceId);
    const fix = { lat: Number(p?.lat ?? p?.latitude), lng: Number(p?.lng ?? p?.longitude) };
    return validFix(fix) ? fix : null;
  } catch { return null; }
}

const stateOf = (d) => ({
  at: toMillis(d?.lastUpdated) || Number(d?.lastUpdatedMs) || 0,
  active: !!d && d.isActive !== false && !liveExpired(toMillis(d.liveUntil), Date.now()),
});

/**
 * @returns {{ fix: {lat:number,lng:number}|null, at: number, active: boolean, busy: boolean, failed: boolean, refresh: () => void }}
 */
export function useLiveFix(liveId, senderId, enabled = true) {
  const [st, setSt] = useState({ fix: null, at: 0, active: true });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    if (!liveId || !enabled) return undefined;
    let gen = 0;
    const un = onSnapshot(doc(db, 'liveLocations', String(liveId)), async (snap) => {
      const my = ++gen;
      const d = snap.exists() ? snap.data() : null;
      const fix = await readFix(d, senderId);
      if (!alive.current || my !== gen) return;
      setSt((prev) => ({ ...stateOf(d), fix: fix || prev.fix }));
    }, () => {});
    return () => { gen += 1; un(); };
  }, [liveId, senderId, enabled]);

  const refresh = useCallback(async () => {
    if (!liveId || busy) return;
    setBusy(true); setFailed(false);
    try {
      const snap = await getDocFromServer(doc(db, 'liveLocations', String(liveId)));
      const d = snap.exists() ? snap.data() : null;
      const fix = await readFix(d, senderId);
      if (!alive.current) return;
      setSt((prev) => ({ ...stateOf(d), fix: fix || prev.fix }));
      if (!fix && stateOf(d).active) setFailed(true);
    } catch {
      if (alive.current) setFailed(true);
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [liveId, senderId, busy]);

  return { ...st, busy, failed, refresh };
}
